/**
 * Sync Panel
 * Phase 2.1: Sync status and controls for offline-first operations
 * Shows sync status, pending operations, and manual sync controls
 * Phase 2.2: Enhanced with conflict resolution UI v2
 */
import React, { useState } from 'react';
import { useSyncQueue } from '../../../hooks/useSyncQueue';
import { useSyncAttention } from '../../../hooks/useSyncAttention';
import { useLicense } from '../../../hooks/useLicense';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';
import { ConflictDrawer } from '../../../conflicts/ConflictDrawer';
import { 
  CloudUpload,
  CloudDownload,
  AlertTriangle,
  CheckCircle,
  Clock,
  Wifi,
  Ban
} from 'lucide-react';

export const SyncPanel = () => {
  const {
    syncing,
    lastSync,
    pendingOps,
    error,
    isSyncEnabled,
    syncNow,
    drainQueue,
    pullChanges
  } = useSyncQueue();
  // FT-577: real outbox failure signal (useSyncQueue is a disabled stub).
  const attention = useSyncAttention();

  const { hasFeature, FEATURES } = useLicense();
  const { showAlert } = useCustomDialogs();
  
  const [conflicts, setConflicts] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [currentConflict, setCurrentConflict] = useState(null);
  const [showConflictDrawer, setShowConflictDrawer] = useState(false);

  // Load conflicts on mount
  React.useEffect(() => {
    const loadConflicts = async () => {
      try {
        // IndexedDB was empty - no conflicts exist
        const conflictRecords = [];
        setConflicts(conflictRecords);
      } catch (err) {
        // Silently handle errors
      }
    };

    loadConflicts();
  }, []);

  const handleManualSync = async () => {
    try {
      const result = await syncNow();
      if (result.success) {
        showAlert(`Sync complete: ${result.drained} sent, ${result.pulled} received`);
      }
    } catch (err) {
      showAlert(`Sync failed: ${err.message}`);
    }
  };

  const handleDrainOnly = async () => {
    try {
      const result = await drainQueue();
      if (result.success) {
        showAlert(`${result.processed} operations sent to server`);
      }
    } catch (err) {
      showAlert(`Failed to send operations: ${err.message}`);
    }
  };

  const handlePullOnly = async () => {
    try {
      const result = await pullChanges();
      if (result.success) {
        showAlert(`${result.applied} changes received from server`);
      }
    } catch (err) {
      showAlert(`Failed to receive changes: ${err.message}`);
    }
  };

  const handleResolveConflicts = () => {
    if (conflicts.length > 0) {
      setCurrentConflict(conflicts[0]);
      setShowConflictDrawer(true);
    }
  };

  const handleConflictResolved = async (resolvedRecord) => {
    if (!currentConflict) return;

    try {
      // NOTE: Audit service and collections removed - conflict resolution disabled
      // This feature requires the new Dexie IndexedDB system

      // Remove the resolved conflict
      const updatedConflicts = conflicts.filter(c => c.id !== currentConflict.id);
      setConflicts(updatedConflicts);

      // Clear current conflict
      setCurrentConflict(null);
      setShowConflictDrawer(false);

      // Show next conflict if any remain
      if (updatedConflicts.length > 0) {
        setCurrentConflict(updatedConflicts[0]);
        setShowConflictDrawer(true);
      } else {
        // All conflicts resolved
        showAlert('All conflicts resolved successfully!');
      }

    } catch (err) {
      showAlert(`Failed to resolve conflict: ${err.message}`);
    }
  };

  const handleConflictDrawerClose = () => {
    setShowConflictDrawer(false);
    setCurrentConflict(null);
  };

  const getConnectionStatus = () => {
    return navigator.onLine ? 'online' : 'offline';
  };

  const getSyncStatus = () => {
    if (!isSyncEnabled) return 'disabled';
    if (syncing) return 'syncing';
    if (error) return 'error';
    // FT-577: failed uploads (e.g. a photo the server rejected) are NOT pending
    // ops — without this the panel fell through to "Synced".
    if (attention.status === 'failed' || attention.status === 'retrying') return 'error';
    if (pendingOps > 0) return 'pending';
    return 'synced';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'syncing': return Clock;
      case 'synced': return CheckCircle;
      case 'pending': return CloudUpload;
      case 'error': return AlertTriangle;
      case 'disabled': return Ban;
      default: return CloudUpload;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'syncing': return 'text-blue-600 bg-blue-50';
      case 'synced': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'error': return 'text-red-600 bg-red-50';
      case 'disabled': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (!hasFeature(FEATURES.CLOUD_SYNC)) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="flex items-center space-x-3">
            <Ban className="h-8 w-8 text-gray-400" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Sync Not Available</h3>
              <p className="text-sm text-gray-500">
                Cloud sync requires a Professional or Fleet plan
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const connectionStatus = getConnectionStatus();
  const syncStatus = getSyncStatus();
  const StatusIcon = getStatusIcon(syncStatus);

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Sync Status</h3>
            <p className="mt-1 text-sm text-gray-500">
              Offline-first sync with automatic background updates
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {connectionStatus === 'online' ? (
              <Wifi className="h-5 w-5 text-green-500" />
            ) : (
              <Ban className="h-5 w-5 text-red-500" />
            )}
            <span className="text-sm font-medium text-gray-700">
              {connectionStatus === 'online' ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Sync Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <StatusIcon className="h-8 w-8 text-gray-400" />
            <div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(syncStatus)}`}>
                  {syncStatus.charAt(0).toUpperCase() + syncStatus.slice(1)}
                </span>
                {pendingOps > 0 && (
                  <span className="text-sm text-gray-500">
                    {pendingOps} pending operation{pendingOps !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {lastSync 
                  ? `Last sync: ${lastSync.toLocaleString()}`
                  : 'Never synced'
                }
              </p>
            </div>
          </div>

          <button
            onClick={handleManualSync}
            disabled={syncing || !navigator.onLine}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncing ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <CloudUpload className="h-4 w-4 mr-2" />
                Sync Now
              </>
            )}
          </button>
        </div>

        {/* FT-577: failed uploads */}
        {attention.attentionMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4" data-testid="sync-panel-failures">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Some changes haven't uploaded
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{attention.attentionMessage}</p>
                </div>
                {attention.canRetry && (
                  <button
                    type="button"
                    onClick={attention.retryNow}
                    disabled={attention.retryInFlight || !navigator.onLine}
                    className="mt-3 inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                  >
                    {attention.retryInFlight ? 'Retrying…' : 'Try again'}
                  </button>
                )}
                {attention.retryNote && (
                  <p className="mt-2 text-sm text-red-700" data-testid="sync-panel-retry-note">{attention.retryNote}</p>
                )}
                {attention.supportNote && (
                  <p className="mt-2 text-sm text-red-700" data-testid="sync-panel-support-note">{attention.supportNote}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Sync Error
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Conflicts */}
        {conflicts.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
            <div className="flex justify-between items-start">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-amber-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">
                    Sync Conflicts
                  </h3>
                  <div className="mt-2 text-sm text-amber-700">
                    <p>
                      {conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} require{conflicts.length === 1 ? 's' : ''} resolution
                    </p>
                    <p className="mt-1 text-xs">
                      Field-level conflicts detected during sync. Review and resolve each conflict.
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={handleResolveConflicts}
                className="inline-flex items-center px-3 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-800 bg-amber-100 hover:bg-amber-200 transition-colors"
              >
                <AlertTriangle className="h-4 w-4 mr-1" />
                Resolve Conflicts
              </button>
            </div>
          </div>
        )}

        {/* Detailed Controls */}
        {showDetails && (
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4">
              Manual Sync Controls
            </h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                onClick={handleDrainOnly}
                disabled={syncing || !navigator.onLine || pendingOps === 0}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CloudUpload className="h-4 w-4 mr-2" />
                Send Changes
              </button>
              
              <button
                onClick={handlePullOnly}
                disabled={syncing || !navigator.onLine}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CloudDownload className="h-4 w-4 mr-2" />
                Receive Changes
              </button>
            </div>
          </div>
        )}

        {/* Sync Statistics */}
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-sm font-medium text-gray-900 mb-4">
            Sync Statistics
          </h4>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Pending Operations</dt>
              <dd className="mt-1 text-sm text-gray-900">{pendingOps}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Conflicts</dt>
              <dd className="mt-1 text-sm text-gray-900">{conflicts.length}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Connection</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {connectionStatus === 'online' ? 'Connected' : 'Offline'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Advanced Toggle */}
        <div className="border-t border-gray-200 pt-4">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-cyan-600 hover:text-cyan-500 font-medium"
          >
            {showDetails ? 'Hide' : 'Show'} Advanced Controls
          </button>
        </div>
      </div>

      {/* Conflict Resolution Drawer */}
      {showConflictDrawer && currentConflict && (
        <ConflictDrawer
          conflict={currentConflict}
          onResolve={handleConflictResolved}
          onClose={handleConflictDrawerClose}
        />
      )}
    </div>
  );
};

export default SyncPanel;